import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { formatAuthError, useForgotPassword } from "@/features/auth/model/useAuth";
import { Button, FormHint, Input, Label } from "@/shared/ui";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordForm() {
  const forgot = useForgotPassword();
  // Flip to the success card the instant the user submits, before the
  // network round-trip resolves. The success copy is intentionally vague
  // ("if an account exists for that address…") to prevent email enumeration,
  // so showing it pre-flight reveals nothing the response wouldn't. On a
  // network error we roll the flag back so the user can retry.
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = handleSubmit((values) => {
    setSubmitted(true);
    forgot.mutate(values, {
      onError: () => setSubmitted(false),
    });
  });

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-success bg-success-light px-4 py-3 text-sm text-success-foreground">
          <p className="font-medium">Check your email</p>
          <p className="mt-1">
            If an account exists for that address, we've sent instructions to reset your
            password. The link expires in 1 hour.
          </p>
        </div>
        <p className="text-center text-sm text-copy-muted">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="text-sm text-copy-muted">
        Enter the email you used to sign up. We'll send a link to reset your password.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          invalid={!!errors.email}
          {...register("email")}
        />
        {errors.email && <FormHint error>{errors.email.message}</FormHint>}
      </div>

      {forgot.error && (
        <div className="rounded-md border border-destructive bg-destructive-light px-3 py-2 text-sm text-destructive-foreground">
          {formatAuthError(forgot.error)}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting || forgot.isPending}
      >
        {forgot.isPending ? "Sending…" : "Send reset link"}
      </Button>

      <p className="text-center text-sm text-copy-muted">
        Remembered it?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      </p>
    </form>
  );
}
